/**
 * Payload shapes for {@link AGENT_RUN_OUTCOME_EVENT_TYPES}.
 *
 * `OperationsEvent.payload` is an untyped JSON column, and every other writer
 * onto that column hand-builds a bare object. That is tolerable for events
 * nobody reads back structurally — a reader shows the JSON and a human reads
 * it. These ARE read back structurally: the run timeline renders an
 * issue key as a link and a HubSpot meeting as an id pair, and a query over
 * `run.linear.issue_created` is only useful if every row carries `issueKey`
 * in the same place. So the shapes are declared once here, in the package
 * both the writer (the backend meeting job) and the reader (the operator
 * dashboard) already import.
 *
 * EVERY FIELD BUT THE IDENTIFYING ONE IS OPTIONAL, deliberately, for the
 * third-party families (`classified` and `phase` are the exceptions, and say
 * why). These payloads describe writes against three third-party systems, and a partial
 * answer is a real outcome: Linear can create an issue and not return a URL,
 * HubSpot can reject a meeting with no error body. A schema that demanded the
 * full set would make the reader reject the event rather than render the half
 * of it that arrived — and an outcome event that fails to parse is exactly
 * the invisible outcome this whole family exists to end.
 *
 * These schemas are for READING a stored payload (`safeParse` at the render
 * boundary) and for TYPING one at the write site. They are deliberately NOT
 * enforced by the recorder: `recordOperationsEvent` takes
 * `Record<string, unknown>` for every domain on the spine, and narrowing it
 * for one family would be a second, type-only write path.
 */

import { z } from 'zod'
import type { AgentRunOutcomeEventType } from './agent-run'
import { PhaseEventPayloadSchema } from './agent-run-phases'
import { ClassifiedPayloadSchema } from './factory-classification'

/** `findings.extracted` — what the model returned for this call, and which model returned it. */
export const FindingsExtractedPayloadSchema = z.object({
  total: z.number().int(),
  actable: z.number().int(),
  parked: z.number().int(),
  /** The configured model id that actually ran, e.g. `openai/gpt-5.6-luna`. */
  model: z.string()
})
export type FindingsExtractedPayload = z.infer<typeof FindingsExtractedPayloadSchema>

/**
 * `school.resolved` — which school the call was with, and which signal said so.
 *
 * `source` is on the payload rather than left in a log line because it is the
 * difference between a CRM-backed answer and a roster-backed one, and that is
 * the first thing anyone asks when a note resolves to the wrong school.
 */
export const SchoolResolvedPayloadSchema = z.object({
  schoolPublicId: z.string(),
  schoolName: z.string(),
  source: z.enum(['hubspot', 'users'])
})
export type SchoolResolvedPayload = z.infer<typeof SchoolResolvedPayloadSchema>

/**
 * `school.unresolved` — no school, and the domains that failed to name one.
 *
 * The domains are the whole point of the event: "no school resolved" is not
 * actionable, "no school resolved for `@wcsmiami.org`" is.
 */
export const SchoolUnresolvedPayloadSchema = z.object({
  externalDomains: z.array(z.string())
})
export type SchoolUnresolvedPayload = z.infer<typeof SchoolUnresolvedPayloadSchema>

/**
 * The four `linear.*` types share one shape.
 *
 * One schema rather than four, because the fields genuinely are the same set
 * with different members present: a created issue has `issueKey`/`issueUrl`,
 * an attached comment adds `commentId`, a created need has `needId`, a failed
 * write has `error` and nothing else. Splitting them into four schemas would
 * mean four near-identical objects and a reader that has to pick one before
 * it knows which it is holding.
 */
export const LinearOutcomePayloadSchema = z.object({
  /** The extraction's own id for the finding this outcome belongs to. */
  findingId: z.string(),
  category: z.string().optional(),
  title: z.string().optional(),
  issueKey: z.string().optional(),
  issueId: z.string().optional(),
  issueUrl: z.string().optional(),
  needId: z.string().optional(),
  commentId: z.string().optional(),
  error: z.string().optional(),
  /**
   * What a SUCCESSFUL write did not manage to do — a lane label that no longer
   * exists, a config row with no `eligible` id, a label merge that rejected
   * after the comment landed. The issue exists and the run is `DONE`, so
   * `error` would be a lie; without this field a half-labelled ticket is
   * invisible until someone notices it never entered a queue.
   *
   * The name predates its widening: the KLEOS triage commit also records a
   * failed customer link (KTD14) and a run row it could not stamp with the
   * issue (KTD7) here, since both are the same kind of fact and a reader
   * querying this field for "what did a filed ticket miss" should find them.
   */
  labelDegradations: z.array(z.string()).optional()
})
export type LinearOutcomePayload = z.infer<typeof LinearOutcomePayloadSchema>

/**
 * The three `hubspot.*` types share one shape, for the same reason the Linear
 * family does: created carries the two ids, skipped carries `reason`, failed
 * carries `reason` and `error`.
 */
export const HubspotOutcomePayloadSchema = z.object({
  meetingId: z.string().optional(),
  companyId: z.string().optional(),
  /** `no-school` / `no-company` / `rejected`, as the destination reported it. */
  reason: z.string().optional(),
  error: z.string().optional()
})
export type HubspotOutcomePayload = z.infer<typeof HubspotOutcomePayloadSchema>

/**
 * `dispatch.intent_recorded` — a finding that WOULD be handed to `/bug` or
 * `/improve`. The intent is recorded and nothing is spawned (KTD6), so this
 * event is the only durable trace that the hand-off was identified at all.
 */
export const DispatchIntentPayloadSchema = z.object({
  findingId: z.string(),
  /** `/bug` or `/improve` — the lane the intent names. */
  kind: z.string(),
  issueKey: z.string().optional()
})
export type DispatchIntentPayload = z.infer<typeof DispatchIntentPayloadSchema>

/**
 * Type -> schema, so a reader holding only an `eventType` string can find the
 * right parser without a switch, and so a test can assert the map is total.
 *
 * `Record` over the closed union, not a lookup with a fallback: adding an
 * outcome type without giving it a payload shape is a compile error here.
 */
export const AGENT_RUN_OUTCOME_EVENT_PAYLOAD_SCHEMAS = {
  'findings.extracted': FindingsExtractedPayloadSchema,
  'school.resolved': SchoolResolvedPayloadSchema,
  'school.unresolved': SchoolUnresolvedPayloadSchema,
  'linear.issue_created': LinearOutcomePayloadSchema,
  'linear.comment_attached': LinearOutcomePayloadSchema,
  'linear.need_created': LinearOutcomePayloadSchema,
  'linear.write_failed': LinearOutcomePayloadSchema,
  'hubspot.meeting_created': HubspotOutcomePayloadSchema,
  'hubspot.meeting_skipped': HubspotOutcomePayloadSchema,
  'hubspot.meeting_failed': HubspotOutcomePayloadSchema,
  'dispatch.intent_recorded': DispatchIntentPayloadSchema,
  // `run.classified` — one classification verdict; see `factory-classification.ts`.
  classified: ClassifiedPayloadSchema,
  // `run.phase` — one step attempt's start or done edge; the shape and its
  // 12,288-character bound live with the rest of the runner's contracts.
  phase: PhaseEventPayloadSchema
} as const satisfies Record<AgentRunOutcomeEventType, z.ZodType>
